/**
 * Shared address search for India (universal: cities, villages, remote, tier cities).
 * Flow: Ola Maps → if 0 results → Google Places → if 0 → Nominatim (OSM).
 * Used by: AddressTimeStep, LocationModal (home), AddressesPage (settings).
 */

const OLA_MAPS_API_KEY = process.env.REACT_APP_OLA_MAPS_API_KEY || '';
const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
const OLA_AUTOCOMPLETE_URL = 'https://api.olamaps.io/places/v1/autocomplete';
const OLA_REVERSE_GEOCODE_URL = 'https://api.olamaps.io/places/v1/reverse-geocode';
const NOMINATIM_SEARCH = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE = 'https://nominatim.openstreetmap.org/reverse';
const GOOGLE_AUTOCOMPLETE = 'https://maps.googleapis.com/maps/api/place/autocomplete/json';
const GOOGLE_PLACE_DETAILS = 'https://maps.googleapis.com/maps/api/place/details/json';

export type PlaceAddress = {
  house_number?: string;
  road?: string;
  suburb?: string;
  neighbourhood?: string;
  city?: string;
  town?: string;
  village?: string;
  state?: string;
  postcode?: string;
  country?: string;
};

export type PlaceSearchResult = {
  place_id: string;
  display_name: string;
  formatted_address?: string;
  lat: number;
  lon: number;
  address: PlaceAddress;
  types?: string[];
  /** If true, call getPlaceDetails(place_id) when user selects to get full address (Google only). */
  needsDetails?: boolean;
};

/** India-focused: try Ola → if 0 results then Google → if 0 then Nominatim. */
export async function searchPlaces(query: string): Promise<PlaceSearchResult[]> {
  const q = (query || '').trim();
  if (q.length < 2) return [];

  // 1) Ola Maps (India)
  if (OLA_MAPS_API_KEY) {
    const olaResults = await searchOlaMaps(q);
    if (olaResults.length > 0) return olaResults;
  }

  // 2) Google Places (fallback – good for villages, remote, tier cities)
  if (GOOGLE_PLACES_API_KEY) {
    const googleResults = await searchGooglePlaces(q);
    if (googleResults.length > 0) return googleResults;
  }

  // 3) Nominatim (OSM) – global fallback
  return searchNominatim(q);
}

/** Get full address for a Google place (call when user selects a result with needsDetails). */
export async function getPlaceDetails(placeId: string): Promise<PlaceSearchResult | null> {
  if (!GOOGLE_PLACES_API_KEY) return null;
  const url = `${GOOGLE_PLACE_DETAILS}?place_id=${encodeURIComponent(placeId)}&fields=geometry,address_components,formatted_address&key=${GOOGLE_PLACES_API_KEY}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.status !== 'OK' || !data.result) return null;
    return mapGoogleDetailsToResult(data.result, placeId);
  } catch {
    return null;
  }
}

/** Extract area_city from reverse-geocoded place (city > town > village > state). */
export function placeToAreaCity(place: PlaceSearchResult | null): string | undefined {
  if (!place?.address) return undefined;
  const { city, town, village, state } = place.address;
  return (city || town || village || state || '').trim() || undefined;
}

/** Reverse geocode: lat,lng → address (Ola first, then Nominatim, then Google if needed). */
export async function reverseGeocode(lat: number, lon: number): Promise<PlaceSearchResult | null> {
  if (OLA_MAPS_API_KEY) {
    const ola = await reverseGeocodeOla(lat, lon);
    if (ola) return ola;
  }
  const nominatim = await reverseGeocodeNominatim(lat, lon);
  if (nominatim) return nominatim;
  return null;
}

// —— Ola Maps ——
async function searchOlaMaps(query: string): Promise<PlaceSearchResult[]> {
  try {
    const res = await fetch(
      `${OLA_AUTOCOMPLETE_URL}?input=${encodeURIComponent(query)}&api_key=${OLA_MAPS_API_KEY}`,
      { method: 'GET', headers: { 'X-Request-Id': '', 'X-Correlation-Id': '' } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    if (data.status !== 'ok' || !Array.isArray(data.predictions)) return [];
    return data.predictions.slice(0, 8).map((p: any) => mapOlaPredictionToResult(p));
  } catch {
    return [];
  }
}

function mapOlaPredictionToResult(p: any): PlaceSearchResult {
  const placeId = p.place_id || p.reference || `${p.geometry?.location?.lat ?? 0}-${p.geometry?.location?.lng ?? 0}`;
  const lat = p.geometry?.location?.lat ?? 0;
  const lng = p.geometry?.location?.lng ?? 0;
  const terms = p.terms || [];
  const address = parseOlaTerms(terms);
  return {
    place_id: placeId,
    display_name: p.description || p.structured_formatting?.main_text || '',
    formatted_address: p.description,
    lat,
    lon: lng,
    address,
    types: p.types || [],
  };
}

function parseOlaTerms(terms: any[]): PlaceAddress {
  const indianStates = [
    'karnataka', 'maharashtra', 'tamil nadu', 'delhi', 'gujarat', 'rajasthan', 'uttar pradesh',
    'west bengal', 'telangana', 'andhra pradesh', 'kerala', 'madhya pradesh', 'bihar', 'odisha',
    'punjab', 'haryana', 'jharkhand', 'assam', 'uttarakhand', 'himachal pradesh', 'chhattisgarh',
    'goa', 'tripura', 'manipur', 'meghalaya', 'sikkim', 'arunachal pradesh', 'mizoram', 'nagaland',
  ];
  let house_number = '';
  let road = '';
  let suburb = '';
  let city = '';
  let state = '';
  let postcode = '';
  let country = '';
  const village = '';
  let town = '';

  const localityCandidates: string[] = [];
  for (const t of terms) {
    const v = (t.value || '').trim();
    const l = v.toLowerCase();
    if (/^\d{6}$/.test(v) && !postcode) postcode = v;
    else if (l === 'india') country = v;
    else if (indianStates.some(s => l.includes(s) || s.includes(l))) state = v;
    else if (l.includes('road') || l.includes('street') || l.includes('main') || l.includes('lane') || l.includes('cross')) road = v;
    else if (l.includes('colony') || l.includes('nagar') || l.includes('sector') || l.includes('layout') || l.includes('society')) { if (!suburb) suburb = v; }
    else if (v.match(/^\d+/) && !house_number && !postcode) house_number = v;
    else localityCandidates.push(v);
  }
  if (localityCandidates.length > 0) {
    town = localityCandidates[0];
    if (localityCandidates.length >= 2) city = localityCandidates[1];
    if (!city) city = town;
  }
  return {
    house_number,
    road,
    suburb,
    neighbourhood: suburb || undefined,
    city: city || town,
    town: town || undefined,
    village: village || undefined,
    state,
    postcode,
    country: country || 'India',
  };
}

// —— Google Places ——
async function searchGooglePlaces(query: string): Promise<PlaceSearchResult[]> {
  try {
    const url = `${GOOGLE_AUTOCOMPLETE}?input=${encodeURIComponent(query)}&key=${GOOGLE_PLACES_API_KEY}&components=country:in`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') return [];
    const predictions = data.predictions || [];
    return predictions.slice(0, 8).map((p: any) => ({
      place_id: p.place_id,
      display_name: p.structured_formatting?.main_text || p.description || '',
      formatted_address: p.description,
      lat: 0,
      lon: 0,
      address: {},
      needsDetails: true,
    }));
  } catch {
    return [];
  }
}

function mapGoogleDetailsToResult(result: any, placeId: string): PlaceSearchResult {
  const comps = result.address_components || [];
  const get = (...types: string[]) => {
    const c = comps.find((x: any) => types.some(t => x.types && x.types.includes(t)));
    return c?.long_name || '';
  };
  const locality = get('locality', 'sublocality', 'sublocality_level_1');
  const village = get('administrative_area_level_3', 'sublocality_level_2');
  const town = get('administrative_area_level_2');
  const city = locality || village || town;
  const state = get('administrative_area_level_1');
  const postcode = get('postal_code');
  const country = get('country') || 'India';
  const street = get('route');
  const house = get('street_number');
  const sublocality = get('sublocality', 'neighborhood');
  const geom = result.geometry?.location || {};
  const lat = typeof geom.lat === 'function' ? geom.lat() : geom.lat ?? 0;
  const lng = typeof geom.lng === 'function' ? geom.lng() : geom.lng ?? 0;
  return {
    place_id: placeId,
    display_name: result.formatted_address || '',
    formatted_address: result.formatted_address,
    lat: Number(lat),
    lon: Number(lng),
    address: {
      house_number: house,
      road: street,
      suburb: sublocality,
      neighbourhood: sublocality,
      city: locality || city,
      town: town || undefined,
      village: village || undefined,
      state,
      postcode,
      country,
    },
  };
}

// —— Nominatim (OSM) ——
async function searchNominatim(query: string): Promise<PlaceSearchResult[]> {
  try {
    const url = `${NOMINATIM_SEARCH}?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=8&countrycodes=in`;
    const res = await fetch(url, { headers: { 'User-Agent': 'OTS-Web-App-India' } });
    if (!res.ok) return [];
    const data = await res.json();
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      place_id: String(item.place_id || `${item.lat}-${item.lon}`),
      display_name: item.display_name || '',
      formatted_address: item.display_name,
      lat: parseFloat(item.lat) || 0,
      lon: parseFloat(item.lon) || 0,
      address: {
        house_number: item.address?.house_number || '',
        road: item.address?.road || '',
        suburb: item.address?.suburb || item.address?.neighbourhood || '',
        neighbourhood: item.address?.neighbourhood || item.address?.suburb || '',
        city: item.address?.city || item.address?.town || item.address?.village || '',
        town: item.address?.town || item.address?.city || '',
        village: item.address?.village || '',
        state: item.address?.state || '',
        postcode: item.address?.postcode || '',
        country: item.address?.country || 'India',
      },
    }));
  } catch {
    return [];
  }
}

async function reverseGeocodeOla(lat: number, lon: number): Promise<PlaceSearchResult | null> {
  try {
    const res = await fetch(
      `${OLA_REVERSE_GEOCODE_URL}?latlng=${lat},${lon}&api_key=${OLA_MAPS_API_KEY}`,
      { method: 'GET', headers: { 'X-Request-Id': '', 'X-Correlation-Id': '' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data.status !== 'ok' || !data.results?.[0]) return null;
    const r = data.results[0];
    const comps = r.address_components || [];
    const get = (types: string[]) => {
      const c = comps.find((x: any) => types.some(t => x.types?.includes(t)));
      return c?.long_name || '';
    };
    const locality = get(['locality', 'administrative_area_level_2']);
    const village = get(['administrative_area_level_3', 'sublocality_level_2']);
    const state = get(['administrative_area_level_1']);
    const postcode = get(['postal_code']);
    return {
      place_id: r.place_id || `ola-${lat}-${lon}`,
      display_name: r.formatted_address || `${lat}, ${lon}`,
      formatted_address: r.formatted_address,
      lat,
      lon,
      address: {
        city: locality || village,
        town: locality,
        village: village || undefined,
        state,
        postcode,
        country: 'India',
      },
    };
  } catch {
    return null;
  }
}

async function reverseGeocodeNominatim(lat: number, lon: number): Promise<PlaceSearchResult | null> {
  try {
    const res = await fetch(
      `${NOMINATIM_REVERSE}?format=json&lat=${lat}&lon=${lon}&addressdetails=1`,
      { headers: { 'User-Agent': 'OTS-Web-App-India' } }
    );
    if (!res.ok) return null;
    const item = await res.json();
    const a = item.address || {};
    return {
      place_id: String(item.place_id || `${lat}-${lon}`),
      display_name: item.display_name || '',
      formatted_address: item.display_name,
      lat,
      lon,
      address: {
        house_number: a.house_number || '',
        road: a.road || '',
        suburb: a.suburb || a.neighbourhood || '',
        neighbourhood: a.neighbourhood || a.suburb || '',
        city: a.city || a.town || a.village || '',
        town: a.town || a.city || '',
        village: a.village || '',
        state: a.state || '',
        postcode: a.postcode || '',
        country: a.country || 'India',
      },
    };
  } catch {
    return null;
  }
}

/**
 * Build form-friendly fields from a PlaceSearchResult (India: village, town, city, remote).
 */
export function placeToFormValues(place: PlaceSearchResult): {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  lat: string;
  long: string;
} {
  const ac = place.address || {};
  const city = ac.city || ac.town || ac.village || '';
  const state = ac.state || '';
  const country = ac.country || 'India';
  const postal_code = ac.postcode || '';
  const addressLine1 = [ac.house_number, ac.road].filter(Boolean).join(' ').trim()
    || place.display_name?.split(',')[0]?.trim()
    || place.formatted_address?.split(',')[0]?.trim()
    || place.display_name
    || 'Address';
  const addressLine2 = ac.suburb || ac.neighbourhood || '';

  return {
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    city,
    state,
    country,
    postal_code,
    lat: String(place.lat),
    long: String(place.lon),
  };
}
